import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";

// Who may approve a NERC CIP audit's report (Audit Management 5A, plan rule R12): the organization's
// CIP Senior Manager and delegates, recorded once for the organization under Assets → NERC sites, with
// their dates. The audit only reads them. The report is approved by the Senior Manager, or a delegate
// whose delegation covers audit reports, holding it on the approval day.

export const NERC_FRAMEWORK = "NERC CIP";

const ROLE_LABEL = { SeniorManager: "CIP Senior Manager", Delegate: "Delegate" };
const showDate = (iso) => (iso ? iso.slice(0, 10) : null);

const AuditCipSeniorManagers = ({ engagement }) => {
    const apiCall = useApi();
    const [holders, setHolders] = useState(null);

    useEffect(() => {
        apiCall("GET", `/security-insights/audits/${engagement.id}/cip-senior-managers`)
            .then(list => setHolders(Array.isArray(list) ? list : []))
            .catch(() => setHolders([]));
    }, [apiCall, engagement.id]);

    if (holders == null) return null;
    // The approvers: the Senior Manager and delegates covering audit reports, during the audit period or now.
    const shown = holders.filter(h => h.mayApproveAuditReports && (h.inAuditPeriod || h.current));
    return (
        <div className="text-xs space-y-1" data-testid={`audit-cip-senior-managers-${engagement.id}`}>
            <div className="font-semibold text-gray-600">CIP Senior Manager and delegates</div>
            <div className="text-gray-500">
                The organization's record, kept under Assets → NERC sites. The report is approved by whoever holds the role on the approval date.
            </div>
            {shown.length === 0
                ? <div className="text-amber-700">No CIP Senior Manager recorded for this audit's period. Record one under Assets → NERC sites.</div>
                : (
                    <ul className="space-y-1">
                        {shown.map(h => (
                            <li key={h.id} data-testid={`audit-cip-holder-${h.id}`} className="flex flex-wrap items-center gap-2">
                                <span className="text-gray-800">{h.displayName}</span>
                                <span className="text-gray-500">{ROLE_LABEL[h.role] || h.role}</span>
                                <span className="text-gray-600">{showDate(h.effectiveFrom)} – {showDate(h.effectiveTo) || "now"}</span>
                                {h.current && <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">Current</span>}
                                {h.left && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">No longer a member</span>}
                                {h.authorityText && <span className="text-gray-500">{h.authorityText}</span>}
                            </li>
                        ))}
                    </ul>
                )}
        </div>
    );
};

export default AuditCipSeniorManagers;
